import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactMapGL, {Source, Layer, WebMercatorViewport, FlyToInterpolator} from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import FilterMapBar from './devices_map/FilterMapBar';
import useDataRepresentationEvent from './devices_map/useDataRepresentationEvent';
import LocationPopup from './devices_map/LocationPopup';
import { boundingBoxFromCollection, dataLineToGeojson, dataPointToGeojson } from './devices_map/utils';
import layerPointStyle from './devices_map/layerPointStyle';
import layerLineStyle from './devices_map/layerLineStyle';

const DeviceMap = ({sptId}) => {
  const mapRef = useRef(null);
  const [viewport, setViewport] = useState({
    width: "100%",
    height: "60vh",
    latitude: 48.863730,
    longitude: 2.357882,
    zoom: 11
  });
  const [data, setData] = useState({type: null, data: []})
  const [currentTooltip, setCurrentTooltip] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const getData = (params) => {
    setIsLoading(true)
    fetch('/v1/visits/sdk_data?' + new URLSearchParams(params), {
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    })
    .then((response) => response.json())
    .then((json) => {
      setIsLoading(false)
      if (params['data_type']=="locations") {
        setData({type: "locations", data: json})
        goToCollection(json)
      } else if (params['data_type']=="visits") {
        setData({type: "visits", data: json})
        goToCollection(json)
      } else if (params['data_type']=="traces") {
        setData({type: "traces", data: json})
        goToCollection(json.flatMap((elt)=>(elt.locations)))
      }
    })
  }
  const goToCollection = (collection) => {
    const {longitude, latitude, zoom} = new WebMercatorViewport(viewport)
        .fitBounds(boundingBoxFromCollection(collection), {
          padding: 20,
          offset: [0, -100]
        });
    setViewport({
      ...viewport,
      longitude,
      latitude,
      zoom,
      transitionDuration: 2000,
      transitionInterpolator: new FlyToInterpolator()
    });
  };
  const pointGeojson = useMemo(() => {
    if (["locations", "visits"].includes(data.type)) {
      return dataPointToGeojson(data.data)
    } else if (data.type == "traces") {
      return dataPointToGeojson(data.data.flatMap((trace)=>(trace.locations)))
    } else {
      return dataPointToGeojson([])
    }
  }, [data]);
  const lineGeojson = useMemo(() => {
    if (data.type == "traces") {
      return dataLineToGeojson(data.data)
    } else {
      return dataLineToGeojson([])
    }
  }, [data]);
  const { onClick, onMouseEnter, onMouseLeave } = useDataRepresentationEvent(
    {
      mapRef,
      onClickIn: (f, e ) => { setCurrentTooltip({info: f.properties, longitude: e.lngLat[0], latitude: e.lngLat[1]}) },
      onClickOut: () => { setCurrentTooltip(null) }
    }
  )

  return (
    <>
      <FilterMapBar sptId={sptId} onValidate={getData} isLoading={isLoading}/>
      <ReactMapGL
        ref={mapRef}
        mapboxApiAccessToken={process.env.MAPBOX_API_ACCESS_TOKEN}
        mapStyle="mapbox://styles/singlespot/cm41gzo3300vq01s849xte181"
        onViewportChange={(viewport) => setViewport(viewport)}
        interactiveLayerIds={["dataPointLayer", "dataLineLayer"]}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        {...viewport}
      >
        <Source id="collectedLineData" type="geojson" data={lineGeojson} generateId lineMetrics>
          <Layer {...layerLineStyle} id="dataLineLayer" />
        </Source>
        <Source id="collectedPointData" type="geojson" data={pointGeojson} generateId>
          <Layer {...layerPointStyle} id="dataPointLayer" />
        </Source>
        { currentTooltip && <LocationPopup setCurrentTooltip={setCurrentTooltip} {...currentTooltip}/> }
      </ReactMapGL>
    </>
  );
}

export default DeviceMap;